import { format } from 'date-fns'

import { IForm } from 'core-components/classes/models/IForm'
import { sanitize } from 'core-components/helpers/sanitize'
import { IColumn } from 'core-components/library/list/types'
import {
  ICrudConstructor,
  ICrudFilters,
  ICrudImplementation,
  ICrudPaging,
  ICrudSorting,
  IDataProviderFilter,
  IDataProviderSort,
  IDataSource,
  IFilter,
  IPaging,
  IPagingFromTo,
  IQueryResult,
  Logic,
  Operator,
  TConnectorFetchFn,
  TQueryKey,
  TSubscriberFn,
} from 'core-components/types'
import { IFetchArgs, IGetByIdArgs } from 'core-components/types/types'

export const errorMessage = 'Method must be implemented'

// T is used for flowed Data types, R is used for type of keyField
export class BaseCrud<T = IForm, R = string | number> {
  public fetcher: ICrudImplementation<T, R>
  public keyField: string
  public hierarchyField: string
  public hierarchyRoot: R | null = null
  public expandedRoots: (R | null)[] = []
  public audience: boolean = false
  public filters: ICrudFilters = {}
  public skipPagingTransform: boolean = false
  protected initialProps: ICrudConstructor<T, R>
  protected connectorFetchFn?: TConnectorFetchFn<T>
  protected orderField?: string
  protected ignoreKeys: Set<R | null> = new Set()
  protected sort: ICrudSorting = {}
  protected paging: ICrudPaging = {
    default: {
      page: 0,
      pageSize: 10,
    },
  }
  protected subscribersCallTimeout: number | null = null
  protected skipSubscribersCallOnFilterChange: any = null

  constructor(props: ICrudConstructor<T, R>) {
    this.fetcher = props.fetcher
    this.keyField = props.keyField
    this.initialProps = { ...props }
    if (props.defaultFilter) {
      this.filters.default = props.defaultFilter
    }
    if (props.defaultSort) {
      this.sort.default = props.defaultSort
    }
    if (props.defaultPaging) {
      this.paging.default = props.defaultPaging
    }
    if (props.audience) {
      this.audience = props.audience
    }
    this.hierarchyField = props.hierarchyField || ''
    if (this.hierarchyField && props.hierarchyRoot !== undefined) {
      this.setRoot(props.hierarchyRoot)
    }
    this.orderField = props.orderField
  }
  public clone = (): BaseCrud<T, R> => {
    return new BaseCrud<T, R>(this.initialProps)
  }

  public getInitialProps = (): ICrudConstructor<T, R> => {
    return { ...this.initialProps }
  }

  public export = async (fileName?: string, columns?: IColumn[]) => {
    if (this.fetcher.export) {
      const filter = this.combineFilter()
      const xlsxColumns =
        columns &&
        columns.map((column) => {
          return {
            title: column.title,
            name: column.field,
          }
        })
      const result = await this.fetcher.export(filter, fileName, xlsxColumns)
      if (!result.Blob) {
        console.error(sanitize('Currently not supported for BaseCrud, use Crud instead'))
        return
      }

      const aLink = document.createElement('a')
      aLink.download = fileName
        ? `${fileName}.xlsx`
        : `${format(new Date(), 'dd-MM-yyyy HH-mm')}-export.xlsx`
      aLink.href = window.URL.createObjectURL(new Blob([result.Blob]))

      if (aLink.href) {
        document.body.appendChild(aLink)
        aLink.click()
        aLink.remove()
        window.URL.revokeObjectURL(aLink.href)
      }
    }
  }

  public getPaging(pagingFromRequest?: IPaging): IPaging {
    const currentPage = pagingFromRequest || this.paging.current || this.paging.default
    return { ...currentPage, startPage: currentPage.startPage || 0 }
  }

  public getFilter() {
    return this.filters.current
  }

  public getDefaultFilter() {
    return this.filters.default
  }

  public combineSort(
    sortFromRequest?: IDataProviderSort | IDataProviderSort[]
  ): IDataProviderSort | IDataProviderSort[] | undefined {
    return sortFromRequest || this.sort.current || this.sort.default
  }

  public setFilter(value: IDataProviderFilter | undefined): void {
    if (JSON.stringify(this.filters.current) !== JSON.stringify(value)) {
      this.filters.current = value
      this.resetStartPage()
      if (this.skipSubscribersCallOnFilterChange !== true) {
        this.callSubscribersWithInstance([])
      }
    }
  }

  public setSkipPagination(value: boolean) {
    this.skipPagingTransform = value
  }

  public setDefaultFilter(value: IDataProviderFilter): void {
    this.filters.default = value
    this.resetStartPage()
    this.callSubscribersWithInstance([])
  }

  private resetStartPage(): void {
    const currentPaging = this.getPaging()
    this.paging.current = { ...currentPaging, page: currentPaging.startPage || 0 }
  }

  public setSort(value: IDataProviderSort | IDataProviderSort[] | undefined): void {
    if (JSON.stringify(this.sort.current) !== JSON.stringify(value)) {
      this.sort.current = value
      this.callSubscribersWithInstance([])
    }
  }

  public setPaging(value: IPaging): void {
    if (JSON.stringify(this.paging.current) !== JSON.stringify(value)) {
      this.paging.current = value
      this.callSubscribersWithInstance([])
    }
  }

  public fetch = async (data: IFetchArgs = {}): Promise<IQueryResult<T, R>> => {
    const { sort, pagingFromTo, query, filter } = this.getRequestParams(data)
    const { force, skipHierarchyFilter, skipTotal } = data
    const fetchedData = await this.query({
      query,
      filter,
      sort,
      paging: pagingFromTo,
      force,
      skipHierarchyFilter,
      skipTotal,
    })
    return fetchedData
  }

  protected async query({
    query,
    filter,
    sort,
    paging,
    force = false,
    skipHierarchyFilter = false,
    skipTotal = false,
  }: {
    query: string
    filter: IDataProviderFilter
    sort: IDataProviderSort | IDataProviderSort[] | undefined
    paging: IPaging | IPagingFromTo
    force?: boolean
    skipHierarchyFilter?: boolean
    skipTotal?: boolean
  }): Promise<IQueryResult<T, R>> {
    const res = await this.fetcher.read(
      query,
      filter,
      sort,
      paging,
      this.keyField,
      this.connectorFetchFn,
      this.hierarchyField,
      this.getExpandedWithoutIgnored(),
      this.hierarchyRoot,
      force,
      skipHierarchyFilter,
      this.orderField,
      this.closeExpandedRoot,
      this.audience,
      skipTotal
    )
    res.total = res.items ? res.items.length : 0
    res.groups = res.groups || []
    return res as IQueryResult<T, R>
  }

  protected getExpandedWithoutIgnored = (): (R | null)[] => {
    const result: (R | null)[] = []
    this.expandedRoots.forEach((el: R | null) => {
      let shouldBeIgnored = false
      if (this.ignoreKeys.has(el)) {
        shouldBeIgnored = true
      }
      if (!shouldBeIgnored) {
        result.push(el)
      }
    })
    return result
  }

  protected getRequestParams = ({ filter, sort, paging, skipHierarchyFilter }: IFetchArgs = {}) => {
    const rFilter = this.combineFilter(filter)
    const sort_ = this.combineSort(sort)
    const rSort = sort_ instanceof Array && sort_.length === 1 ? sort_[0] : sort_
    const rPaging = this.getPaging(paging)
    const pagingFromTo: IPaging | IPagingFromTo = this.skipPagingTransform
      ? rPaging
      : this.pagingTransform(rPaging)
    this.checkFilters(rFilter)
    const audience = this.audience
    const queryKey: TQueryKey = [rFilter, rSort, pagingFromTo, audience, !!skipHierarchyFilter]
    const query = JSON.stringify(queryKey)
    return { filter: rFilter, sort: rSort, paging: rPaging, pagingFromTo, query }
  }

  protected checkFilters = (filter: IDataProviderFilter) => {
    filter.filters.map((f) => {
      if ((f as IDataProviderFilter).filters) {
        this.checkFilters(f as IDataProviderFilter)
      } else {
        if ((f as IFilter).operator === 'eq' && (f as IFilter).value === undefined) {
          console.error('problem with filters', f)
        }
      }
    })
  }

  protected pagingTransform = (paging: IPaging): IPagingFromTo => ({
    from: paging.page * paging.pageSize,
    to: (paging.page + 1) * paging.pageSize,
  })

  protected combineFilter(filterFromRequest?: IDataProviderFilter): IDataProviderFilter {
    if (filterFromRequest) {
      return {
        ...filterFromRequest,
        filters: [...filterFromRequest.filters, ...(this.filters.default?.filters || [])],
      }
    }
    const map: any = {}
    this.filterToFlat(map, (this.filters.default?.filters as IFilter[]) || [])
    this.filterToFlat(map, (this.filters.current?.filters as IFilter[]) || [])
    this.filterToFlat(map, (this.filters.permanent?.filters as IFilter[]) || [])

    const filterResult: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [],
    }
    delete map['notFlatten']
    Object.keys(map).forEach((field) => {
      if (map[field] && map[field].length) {
        map[field].forEach((one: any) => {
          filterResult.filters.push(one)
        })
      } else {
        filterResult.filters.push(map[field])
      }
    })
    return filterResult
  }

  protected filterToFlat(map: any, filters: IFilter[]) {
    if (!map['notFlatten']) {
      map['notFlatten'] = 0
    }
    filters.forEach((el) => {
      if (el.field) {
        if (el.operator === Operator.EQUAL && el.value === undefined) {
          delete map[el.field]
        } else if (el.operator === Operator.EQUAL) {
          map[el.field] = [el]
        } else {
          const current = map[el.field] || []
          map[el.field] = [...current, el]
        }
      } else {
        map['notFlatten']++
        map[`notFlatten-${map['notFlatten']}`] = el
      }
    })
  }

  public setRoot(value: R | null): void {
    if (value !== this.hierarchyRoot) {
      this.hierarchyRoot = value
      this.callSubscribersWithInstance([])
    }
  }

  public callSubscribersWithInstance = (result: T[] | boolean): void => {
    if (!this.fetcher?.subscribersCall) {
      return
    }
    if (!this.subscribersCallTimeout) {
      this.subscribersCallTimeout = window.setTimeout(() => {
        if (this.fetcher.subscribersCall) {
          this.fetcher.subscribersCall(result)
        }
        if (this.subscribersCallTimeout) {
          clearTimeout(this.subscribersCallTimeout)
        }
        this.subscribersCallTimeout = null
      }, 0)
    }
  }

  public getRoot = () => this.hierarchyRoot

  public async openNodes(values: (R | null)[]): Promise<IQueryResult<T, R>> {
    const { filter, sort, pagingFromTo, query } = this.getRequestParams()
    this.appendExpandedRoot(values)
    const result = await this.query({ query, filter, sort, paging: pagingFromTo })
    this.addEmptyNodesToIgnoredKeys(values, result)
    return result
  }

  private appendExpandedRoot = (values: (R | null)[]) => {
    values.forEach((value) => {
      if (!this.expandedRoots.find((el) => el === value) && value !== this.hierarchyRoot) {
        this.expandedRoots.push(value)
      }
    })
  }

  private addEmptyNodesToIgnoredKeys = (values: (R | null)[], result: IQueryResult<T, R>) => {
    values.forEach((value) => {
      if (result.groups) {
        let emptyNode = true
        result.groups.forEach((group) => {
          if (group[0] && group[0][this.hierarchyField] === value) {
            emptyNode = false
          }
        })
        if (emptyNode) {
          this.ignoreKeys.add(value)
        }
      }
    })
  }

  protected getParamsGetById = (args: R | null | undefined | IGetByIdArgs<R>) => {
    if (typeof args === 'object' && args !== null && 'id' in args) {
      const { id, force, keyField } = args

      return { id, force: force || false, keyField: keyField || this.keyField }
    } else {
      return { id: args, force: false, keyField: this.keyField }
    }
  }

  public getById = async (args: R | null | undefined | IGetByIdArgs<R>): Promise<T | null> => {
    const { id, force, keyField } = this.getParamsGetById(args)
    if (!id) {
      return null
    }
    const { filter, sort } = this.getRequestParams()
    filter.filters.push({
      value: id as string,
      field: keyField || this.keyField,
      operator: Operator.EQUAL,
    })
    const paging: IPagingFromTo = { from: 0, to: 0 }
    const query = JSON.stringify([filter, sort, paging])
    if (!force) {
      const cached = await this.tryToFindInCache(id, query, keyField)
      if (cached) {
        return cached
      }
    }
    const fetchedData = await this.query({
      query,
      filter,
      sort,
      paging,
      force,
      skipHierarchyFilter: true,
    })
    return this.filterFetchedById(fetchedData, id, keyField)
  }

  protected filterFetchedById = (fetchedData: IQueryResult<T, R>, value: R, keyField?: string) => {
    if (fetchedData.items) {
      const item = fetchedData.items.find(
        // tslint:disable-next-line:triple-equals
        (el) => el[keyField || this.keyField] == value
      )
      if (item) {
        return item
      }
    }
    return null
  }

  protected tryToFindInCache = async (value: R, query: string, keyField?: string) => {
    if (this.fetcher.readById) {
      const item = await this.fetcher.readById(
        value,
        keyField || this.keyField,
        query,
        this.hierarchyField,
        this.closeExpandedRoot
      )
      if (item.items.length) {
        return item.items[0] as T
      }
    }
    return null
  }

  public subscribe = (event: string, callBack: TSubscriberFn<T>): (() => void) | undefined => {
    return this.fetcher.subscribe && this.fetcher.subscribe(event, callBack as TSubscriberFn<T>)
  }

  public saveToStore = async ({
    query,
    data,
    hierarchyField,
  }: {
    query: string
    data: {
      items: T[]
      total: number
    }
    hierarchyField?: string
  }): Promise<void> => {
    throw new Error(errorMessage)
  }

  public getDataFromStore = (): void => {
    throw new Error(errorMessage)
  }

  public async clear(): Promise<void> {
    throw new Error(errorMessage)
  }

  public deleteBatch = async (
    formId: string | IDataSource,
    model: IForm[],
    keyField?: string
  ): Promise<boolean> => {
    throw new Error(errorMessage)
  }

  public removeQueries = (callSubscribers: boolean = false): void => {
    throw new Error(errorMessage)
  }

  /* Args
     callSubscribers?: boolean
   */
  public async clearData() {}

  public create = async (formId: string | IDataSource, models: IForm[]): Promise<T[]> => {
    return this.fetcher.create(typeof formId === 'string' ? parseInt(formId, 10) : formId, models)
  }

  public update = async (formId: string | IDataSource, models: IForm[]): Promise<T[]> => {
    return this.fetcher.update(typeof formId === 'string' ? parseInt(formId, 10) : formId, models)
  }

  public delete = async (
    formId: string | IDataSource,
    model: IForm,
    keyField?: string
  ): Promise<boolean> => {
    const keyF = keyField || this.keyField
    const deleteMethod = this.fetcher.delete

    if (keyF && deleteMethod) {
      await deleteMethod(
        typeof formId === 'string' ? parseInt(formId, 10) : formId,
        model[keyF],
        keyF
      )
    }
    return true
  }

  public has = (keyField: string, keyValue: string): boolean => {
    if (this.fetcher.searchInCache) {
      const result = this.fetcher.searchInCache(keyField || this.keyField, keyValue)
      return !!result
    }
    return false
  }

  public triggerOnChange = (arg: T[] = []) => {
    this.callSubscribersWithInstance(arg)
  }

  public closeExpandedRoot = (root: R | null) => {
    const index = this.expandedRoots.findIndex((el) => el === root)
    if (index > -1) {
      this.expandedRoots.splice(index, 1)
    }
  }

  public setHierarchyField(value: string) {
    this.hierarchyField = value
  }
}
